/**
 * Reading a latitude or longitude the way people actually type it.
 *
 * Accepted forms, for either axis:
 * - decimal degrees: 40.7128, −74.006, 40,7128 (decimal comma), 40.7128° N
 * - degrees and decimal minutes: 40° 42.768′ N, 40 42.768 N
 * - degrees, minutes and seconds: 40° 42′ 46.08″ N, 40 42 46.08 N, 40°42'46"N
 * A hemisphere letter (N, S, E, W) may lead or trail; S and W make the value negative.
 * A leading minus sign does the same, but not together with S or W.
 */

export type CoordinateAxis = "lat" | "lon";

export type CoordinateError =
  | "empty"
  | "format"
  | "range"
  | "minutes"
  | "seconds"
  | "hemisphere"
  | "axis";

export type CoordinateNotation = "decimal" | "ddm" | "dms";

export type ParsedCoordinate =
  | { ok: true; value: number; notation: CoordinateNotation }
  | { ok: false; error: CoordinateError };

export const MAX_LATITUDE = 90;
export const MAX_LONGITUDE = 180;

const HEMISPHERE_LEADING = /^([NSEW])\s*(.*)$/i;
const HEMISPHERE_TRAILING = /^(.*?)\s*([NSEW])$/i;
const NUMBER_TOKEN = /[-+−]?\d+(?:[.,]\d+)?|[.,]\d+/g;
const MARKERS = /^[\s°º˚'′’"″”]*$/;
const MINUS_SIGNS = /[−–]/g;
const LEADING_SIGN = /^[-+]/;
const DECIMAL_MARK = /[.,]/;
const SECONDS_AS_QUOTES = /''/g;

function readHemisphere(text: string): { rest: string; letter: string | null } {
  const trailing = HEMISPHERE_TRAILING.exec(text);
  if (trailing) {
    return { rest: trailing[1], letter: trailing[2].toUpperCase() };
  }
  const leading = HEMISPHERE_LEADING.exec(text);
  if (leading) {
    return { rest: leading[2], letter: leading[1].toUpperCase() };
  }
  return { rest: text, letter: null };
}

function toNumber(token: string): number {
  return Number(token.replace(",", "."));
}

const NOTATIONS: CoordinateNotation[] = ["decimal", "ddm", "dms"];

function hasValidAxis(letter: string | null, axis: CoordinateAxis): boolean {
  if (letter === null) {
    return true;
  }
  return (axis === "lat" ? "NS" : "EW").includes(letter);
}

interface Parts {
  degrees: number;
  minutes: number;
  seconds: number;
}

/** The numeric tokens as degrees, minutes, seconds — or the reason they do not fit. */
function readParts(tokens: string[]): Parts | CoordinateError {
  if (tokens.slice(1).some((token) => LEADING_SIGN.test(token))) {
    return "format";
  }
  // Only the last number may carry a fraction: 40° 42.5′ is fine, 40.5° 42′ is not.
  if (tokens.slice(0, -1).some((token) => DECIMAL_MARK.test(token))) {
    return "format";
  }
  const [degrees, minutes = 0, seconds = 0] = tokens.map((token, index) =>
    index === 0 ? Math.abs(toNumber(token)) : toNumber(token)
  );
  if (minutes >= 60) {
    return "minutes";
  }
  return seconds >= 60 ? "seconds" : { degrees, minutes, seconds };
}

/** Parse one coordinate for the given axis. Never throws. */
export function parseCoordinate(
  raw: string,
  axis: CoordinateAxis
): ParsedCoordinate {
  const normalized = raw
    .trim()
    .replace(MINUS_SIGNS, "-")
    .replace(SECONDS_AS_QUOTES, '"');
  if (normalized === "") {
    return { ok: false, error: "empty" };
  }
  const { rest, letter } = readHemisphere(normalized);
  if (!hasValidAxis(letter, axis)) {
    return { ok: false, error: "axis" };
  }
  const tokens = rest.match(NUMBER_TOKEN) ?? [];
  const leftover = rest.replace(NUMBER_TOKEN, " ");
  if (tokens.length === 0 || tokens.length > 3 || !MARKERS.test(leftover)) {
    return { ok: false, error: "format" };
  }
  const negativeSign = (tokens[0] ?? "").startsWith("-");
  if (negativeSign && letter !== null) {
    return { ok: false, error: "hemisphere" };
  }
  const parts = readParts(tokens);
  if (typeof parts === "string") {
    return { ok: false, error: parts };
  }
  const magnitude = parts.degrees + parts.minutes / 60 + parts.seconds / 3600;
  const limit = axis === "lat" ? MAX_LATITUDE : MAX_LONGITUDE;
  if (magnitude > limit) {
    return { ok: false, error: "range" };
  }
  const negative = negativeSign || letter === "S" || letter === "W";
  return {
    ok: true,
    value: (negative ? -magnitude : magnitude) || 0,
    notation: NOTATIONS[tokens.length - 1],
  };
}
